import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { WIDGET_GROUPS } from '../utils/dashboardWidgets';

export default function DashboardCustomize({
  widgets,
  activeIds,
  onToggle,
  onSetEnabled,
  onReset,
}) {
  const [query, setQuery] = useState('');
  const active = useMemo(() => new Set(activeIds), [activeIds]);
  const normalizedQuery = query.trim().toLowerCase();
  const shownCount = widgets.filter((widget) => active.has(widget.id)).length;

  const matches = (widget) => {
    if (!normalizedQuery) return true;
    return `${widget.label} ${widget.description || ''}`.toLowerCase().includes(normalizedQuery);
  };

  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid var(--rs-border)',
        bgcolor: 'var(--rs-paper)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Customize dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {shownCount} of {widgets.length} widgets on your dashboard. Drag the handle on a card to move it, or the corner to resize.
          </Typography>
        </Box>
        <Button
          variant="text"
          size="small"
          startIcon={<RestartAltIcon fontSize="small" />}
          onClick={onReset}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          Reset to default
        </Button>
      </Box>

      {widgets.length > 4 && (
        <TextField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          size="small"
          fullWidth
          placeholder="Search stations and widgets"
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      )}

      <Box sx={{ display: 'grid', gap: 2.5 }}>
        {WIDGET_GROUPS.map((group) => {
          const groupWidgets = widgets.filter((widget) => widget.group === group.id);
          const visibleWidgets = groupWidgets.filter(matches);
          if (groupWidgets.length === 0 || visibleWidgets.length === 0) return null;

          const bulkTargets = normalizedQuery ? visibleWidgets : groupWidgets;
          const allOn = bulkTargets.every((widget) => active.has(widget.id));
          const allOff = bulkTargets.every((widget) => !active.has(widget.id));

          return (
            <Box key={group.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="overline" sx={{ color: 'var(--rs-accent)', fontWeight: 800, letterSpacing: '0.12em', lineHeight: 1.4 }}>
                  {group.title}
                </Typography>
                {bulkTargets.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    <Button size="small" disabled={allOn} onClick={() => onSetEnabled(bulkTargets, true)}>
                      Show all
                    </Button>
                    <Button size="small" disabled={allOff} onClick={() => onSetEnabled(bulkTargets, false)} sx={{ color: 'text.secondary' }}>
                      Hide all
                    </Button>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {group.hint}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 0.75,
                  maxHeight: group.id === 'now-playing' ? 280 : 'none',
                  overflow: 'auto',
                  pr: 0.25,
                }}
              >
                {visibleWidgets.map((widget) => {
                  const enabled = active.has(widget.id);
                  return (
                    <Box
                      key={widget.id}
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: enabled ? 'rgba(34,211,238,0.4)' : 'var(--rs-border)',
                        bgcolor: enabled ? 'rgba(34,211,238,0.06)' : 'transparent',
                      }}
                    >
                      <FormControlLabel
                        sx={{ alignItems: 'center', mx: 0, width: '100%', py: 0.5 }}
                        control={(
                          <Switch
                            checked={enabled}
                            onChange={() => onToggle(widget.id)}
                            inputProps={{ 'aria-label': `${enabled ? 'Hide' : 'Show'} ${widget.label}` }}
                          />
                        )}
                        label={(
                          <Box sx={{ minWidth: 0, py: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {widget.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {widget.description}
                              {' · '}
                              {enabled ? 'On your dashboard' : 'Available'}
                            </Typography>
                          </Box>
                        )}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      {normalizedQuery && widgets.every((widget) => !matches(widget)) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nothing matches “{query.trim()}”.
        </Typography>
      )}
    </Box>
  );
}
